import { InputPrice } from '@/components/element/Form/InputPrice';
import { SimpleTextFormat } from '@/components/element/SimpleTextFormat';
import { useUpdatePaymentAmount } from '@/hooks/api/1130_update_payment_amount';
import { usePaymentTaskFixedAssetsInvalidate } from '@/hooks/api/1150_payment_task_fixed_assets';
import { useInvalidatePaymentTaskExpenseAccountItems } from '@/hooks/api/2350_payment_task_expense_account_items';
import { toastApiError } from '@/lib/toast';
import { useLoading } from '@/providers/LoadingProvider';
import { useCurrentPaymentTask } from '@/providers/PaymentTaskProvider';
import { useCurrentTeamMember } from '@/providers/TeamMemberProvider';
import { useEffect, useMemo, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { ConsumptionTaxRuleLogic } from '@/hooks/api/models/logics';
import { useInvalidateOrderAllocatedExpenses } from '@/hooks/api/1192_order_allocated_expenses';

type FormInputs = {
  identifier: string;
  team_id: number;
  purchase_order_id: number;
  task_id: number;
  payment_amount: number;
  payment_amount_including_tax?: number;
  payment_amount_description?: string;
  withholding_tax_amount: number;
};
export const PaymentAmount = ({ disabled }: { disabled: boolean }) => {
  const { setShowLoading } = useLoading();
  const { _w_, _t_, me, meTeamMember } = useCurrentTeamMember();
  const { purchase_order_id } = useParams();
  const { paymentTask: task, setPaymentTask } = useCurrentPaymentTask();
  const [editing, setEditing] = useState<boolean>(false);
  const { mutate, data, isSuccess, error, isPending } = useUpdatePaymentAmount();
  const { invalidate: invalidatePaymentTaskFixedAssets } = usePaymentTaskFixedAssetsInvalidate({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    purchase_order_id: Number(purchase_order_id),
    task_id: task!.id,
  });
  const { invalidate: invalidatePaymentTaskExpenseAccountItems } = useInvalidatePaymentTaskExpenseAccountItems({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    purchase_order_id: Number(purchase_order_id),
    task_id: task!.id,
  });
  const { invalidate: invalidateOrderAllocatedExpenses } = useInvalidateOrderAllocatedExpenses({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    purchase_order_id: Number(purchase_order_id),
    task_id: task!.id,
  });
  const {
    control,
    register,
    handleSubmit,
    getValues,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormInputs>({
    mode: 'onBlur',
    criteriaMode: 'all',
    defaultValues: {
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      purchase_order_id: Number(purchase_order_id),
      task_id: task!.id,
      payment_amount: Number(task!.payment_amount),
      payment_amount_including_tax:
        task!.business_partner_context.name == 'qualified' ? Number(task!.payment_amount_including_tax) : undefined,
      payment_amount_description: task!.payment_amount_description,
      withholding_tax_amount: task!.withholding_tax_amount != null ? task!.withholding_tax_amount : 0,
    },
  });

  // 支払額
  const [transferAmount, setTransferAmount] = useState(0);

  const [paymentAmountIncludingTax] = watch(['payment_amount_including_tax']);

  // 源泉徴収額変更時に振込額を設定
  const [withHoldingTaxAmount] = watch(['withholding_tax_amount']);
  useEffect(() => {
    if (isQualified) {
      const paymentAmountIncludingTax = getValues('payment_amount_including_tax') || 0;
      setTransferAmount(paymentAmountIncludingTax - withHoldingTaxAmount);
    } else {
      const paymentAmount = getValues('payment_amount') || 0;
      setTransferAmount(paymentAmount - withHoldingTaxAmount);
    }
  }, [withHoldingTaxAmount, data]);

  // 適格事業者かどうか
  const isQualified = useMemo(() => task!.business_partner_context.name == 'qualified', [task]);

  useEffect(() => setShowLoading(isPending), [isPending]);
  useEffect(() => toastApiError(error), [error]);

  const onSubmit: SubmitHandler<FormInputs> = (inputData) => {
    setShowLoading(true);
    mutate(inputData);
  };

  useEffect(() => {
    if (isSuccess && data) {
      setEditing(false);
      setPaymentTask(data);
      console.log('data', data);
      invalidatePaymentTaskFixedAssets();
      invalidatePaymentTaskExpenseAccountItems();
      invalidateOrderAllocatedExpenses();
    }
  }, [isSuccess]);

  return (
    <div
      className={`checklist-item ps-2 ms-3 ${
        task!.payment_amount != null ? 'checklist-item-success' : 'checklist-item-secondary'
      }`}
    >
      <div className="ms-1">
        <h6 className="mb-0 text-dark font-weight-bold text-sm">支払金額の確定</h6>
        <div className="text-weak text-xs">請求書に記載された金額を入力して下さい</div>
      </div>

      <div className="ms-1 mt-3 ps-1">
        <table className="table align-items-center">
          <thead>
            <tr>
              <th
                className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 px-2 text-end"
                style={{ width: '40%' }}
              >
                {isQualified ? '合計金額（税込）' : ' '}
              </th>
              <th
                className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 px-2 text-end"
                style={{ width: '40%' }}
              >
                {isQualified ? '合計金額（税抜）' : '総額'}
              </th>
              <th
                className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 px-2 text-end"
                style={{ width: '20%' }}
              >
                計画金額（税抜）
              </th>
            </tr>
          </thead>
          <tbody>
            {!editing && (
              <tr>
                <td className="text-xs text-dark text-end px-2">
                  {task!.payment_amount_including_tax != null
                    ? `¥${task!.payment_amount_including_tax!.toLocaleString()}`
                    : ' '}
                </td>
                <td className="text-xs text-dark text-end px-2">
                  {task!.payment_amount != null ? `¥${task!.payment_amount.toLocaleString()}` : '-'}
                </td>
                <td className="text-xs text-dark text-end px-2">
                  ¥{task!.payment_schedule_detail.amount.toLocaleString()}
                </td>
              </tr>
            )}
            {editing && (
              <tr>
                <td className="text-xs">
                  {isQualified && (
                    <div className="input-group">
                      <Controller
                        render={({ field: { onChange, value, ...field } }) => (
                          <InputPrice onChange={onChange} value={value} error={errors.payment_amount_including_tax} />
                        )}
                        control={control}
                        name="payment_amount_including_tax"
                        rules={{ required: task!.business_partner_context.name == 'qualified' }}
                      />
                    </div>
                  )}
                </td>
                <td className="text-xs text-wrap">
                  <div className="input-group">
                    <Controller
                      render={({ field: { onChange, value, ...field } }) => (
                        <InputPrice
                          onChange={onChange}
                          onBlur={(val) => {
                            if (
                              task!.business_partner_context.name == 'qualified' &&
                              val != null &&
                              Number(val) > 0 &&
                              paymentAmountIncludingTax == 0
                            ) {
                              setValue(
                                'payment_amount_including_tax',
                                ConsumptionTaxRuleLogic.calc(Number(val), _w_!.consumption_tax_rule)
                              );
                            }
                          }}
                          value={value}
                          error={errors.payment_amount}
                        />
                      )}
                      control={control}
                      name="payment_amount"
                      rules={{ required: true }}
                    />
                  </div>
                </td>
                <td className="text-xs text-dark text-end px-3">
                  ¥{task!.payment_schedule_detail.amount.toLocaleString()}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <table className="table align-items-center">
          <thead>
            <tr>
              <th
                className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 px-2 text-end"
                style={{ width: '40%' }}
              >
                &nbsp;
              </th>
              <th
                className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 px-2 text-end"
                style={{ width: '40%' }}
              >
                源泉徴収税額
              </th>
              <th
                className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 px-2 text-end"
                style={{ width: '20%' }}
              >
                振込額
              </th>
            </tr>
          </thead>
          <tbody>
            {!editing && (
              <tr>
                <td className="text-xs text-dark text-end px-2">&nbsp;</td>
                <td className="text-xs text-dark text-end px-2">
                  ¥{task!.withholding_tax_amount != null ? task!.withholding_tax_amount.toLocaleString() : 0}
                </td>
                <td className="text-xs text-dark text-end px-2">¥{transferAmount.toLocaleString()}</td>
              </tr>
            )}
            {editing && (
              <tr>
                <td className="text-xs">&nbsp;</td>
                <td className="text-xs text-wrap">
                  <div className="input-group">
                    <Controller
                      render={({ field: { onChange, value, ...field } }) => (
                        <InputPrice onChange={onChange} value={value} error={errors.withholding_tax_amount} />
                      )}
                      control={control}
                      name="withholding_tax_amount"
                      rules={{ required: true }}
                    />
                  </div>
                </td>
                <td className="text-xs text-dark text-end px-3">¥{transferAmount.toLocaleString()}</td>
              </tr>
            )}
          </tbody>
        </table>
        {editing ? (
          <div className="form-group">
            <label>金額メモ</label>
            <textarea
              className="form-control"
              maxLength={2000}
              rows={5}
              {...register('payment_amount_description')}
            ></textarea>
          </div>
        ) : (
          <div className="form-group">
            <label className="mb-0">金額メモ</label>
            <p className="text-sm ms-1">
              <SimpleTextFormat content={task!.payment_amount_description || ''} />
            </p>
          </div>
        )}
        <div className="d-flex justify-content-end">
          {editing ? (
            <>
              <button className="btn btn-icon btn-outline-secondary" onClick={() => setEditing(false)}>
                <i className="fas fa-undo"></i>キャンセル
              </button>
              <button className="btn btn-icon bg-gradient-dark ms-2" onClick={() => handleSubmit(onSubmit)()}>
                <i className="fas fa-check-circle"></i>確定
              </button>
            </>
          ) : (
            <button
              className={`btn btn-icon bg-gradient-success ms-2 ${disabled && 'disabled'}`}
              onClick={() => setEditing(true)}
            >
              <i className="fas fa-edit"></i>編集
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
